'use client'

import '/node_modules/flag-icons/css/flag-icons.min.css'
import { useMutation } from '@apollo/client/react'
import { useLocale, useTranslations } from 'next-intl'
import { UPSERT_USER_LOCALE } from '@/graphql/mutations/upsertLocale'
import { locales } from '@/i18n/request'
import { usePathname, useRouter } from '@/navigation'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/shared/ui/select'
import { Locale } from '@/types/locale'

// Flag shown before each language
const flagByLocale: Record<Locale, string> = {
  fr: 'fr',
  en: 'gb',
  nl: 'nl'
}

// Language of the subflow chosen by the prospect, saved on its account and sent to Kameleoon
const CountryLangSelector = () => {
  const t = useTranslations('common')
  const locale = useLocale() as Locale
  const pathname = usePathname()
  const router = useRouter()

  const [upsertUserLocale] = useMutation(UPSERT_USER_LOCALE)

  const items = Object.fromEntries(
    locales.map((lang) => [
      lang,
      <span key={lang} className='flex items-center gap-3.5'>
        <span aria-hidden className={`fi fi-${flagByLocale[lang]}`} />
        {t(lang)}
      </span>
    ])
  )

  // Saves the language chosen, then opens the current step in this language
  const handleChange = (value: Locale | null) => {
    if (!value) return
    upsertUserLocale({ variables: { locale: value.toUpperCase() } })
    window.kameleoonQueue?.push(['Goals.processConversion', 343663])
    router.replace(pathname, { locale: value })
  }

  return (
    <Select<Locale> items={items} value={locale} onValueChange={handleChange}>
      <SelectTrigger
        aria-label={t('language')}
        className='h-11 gap-3.5 rounded-xl border-2 border-border bg-white pr-2.5 pl-3 text-base text-[color:var(--gray-600)] data-[size=default]:h-11 [&>svg:last-child]:size-6 [&>svg:last-child]:text-[color:var(--gray-600)]'
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {locales.map((lang) => (
          <SelectItem key={lang} value={lang} className='text-base'>
            {items[lang]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export default CountryLangSelector
